import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';

/**
 * Validates URL for security - only allows http(s) protocols
 * Prevents javascript: URIs and other potentially malicious schemes
 * @param {string} url - URL to validate
 * @returns {boolean} - true if URL is safe to use
 */
function isValidImageUrl(url) {
  if (!url || typeof url !== 'string') return false;
  
  const trimmed = url.trim();
  if (!trimmed) return false;
  
  // Only allow http:// and https:// protocols
  // Block javascript:, data:, vbscript:, etc.
  try {
    const parsed = new URL(trimmed);
    return ['http:', 'https:'].includes(parsed.protocol);
  } catch {
    // If URL parsing fails, check if it's a relative path (starts with /)
    return trimmed.startsWith('/') && !trimmed.startsWith('//');
  }
}

/**
 * Composable to access custom Captain/Copilot icon configuration.
 * Returns the custom icon URL if configured and valid, otherwise uses default icon.
 * Includes security validation to prevent XSS via malicious URLs.
 * Supports dark/light theme switching.
 */
export function useCaptainIcon() {
  const store = useStore();
  
  // Track if the image failed to load (to fallback to default icon)
  const imageLoadFailed = ref(false);
  
  // Track dark mode state reactively
  const isDarkMode = ref(false);

  // Update dark mode state based on body class
  const updateDarkMode = () => {
    isDarkMode.value = document.body.classList.contains('dark');
  };

  // Setup MutationObserver to watch for dark mode changes
  let observer = null;
  
  onMounted(() => {
    updateDarkMode();
    
    // Watch for class changes on body to detect theme switching
    observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.attributeName === 'class') {
          updateDarkMode();
          // Reset image error state when theme changes (different URL might work)
          imageLoadFailed.value = false;
        }
      });
    });
    
    observer.observe(document.body, { attributes: true });
  });

  onUnmounted(() => {
    if (observer) {
      observer.disconnect();
    }
  });

  const rawCaptainIconUrl = computed(() => {
    // Null-safe access to globalConfig getter
    const globalConfig = store?.getters?.['globalConfig/get'];
    return globalConfig?.captainIconUrl ?? '';
  });

  const rawCaptainIconDarkUrl = computed(() => {
    // Null-safe access to globalConfig getter
    const globalConfig = store?.getters?.['globalConfig/get'];
    return globalConfig?.captainIconDarkUrl ?? '';
  });

  const captainIconUrl = computed(() => {
    // Choose URL based on current theme
    // If dark mode: prefer dark URL, fallback to light URL
    // If light mode: use light URL only
    let url = '';
    
    if (isDarkMode.value) {
      // In dark mode: prefer dark URL, fallback to light
      const darkUrl = rawCaptainIconDarkUrl.value;
      const lightUrl = rawCaptainIconUrl.value;
      url = (isValidImageUrl(darkUrl) ? darkUrl : lightUrl);
    } else {
      // In light mode: use light URL
      url = rawCaptainIconUrl.value;
    }
    
    // Only return URL if it passes security validation
    if (isValidImageUrl(url)) {
      return url.trim();
    }
    return '';
  });

  const hasCaptainIcon = computed(() => {
    // Only show custom icon if URL is valid AND image hasn't failed to load
    return captainIconUrl.value !== '' && !imageLoadFailed.value;
  });

  /**
   * Call this when the image fails to load to fallback to default icon
   */
  const handleImageError = () => {
    imageLoadFailed.value = true;
  };

  /**
   * Reset the error state (useful if URL is updated)
   */
  const resetImageError = () => {
    imageLoadFailed.value = false;
  };

  return { 
    captainIconUrl, 
    hasCaptainIcon, 
    handleImageError,
    resetImageError 
  };
}
